import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { ShopService } from 'src/app/services/shop/shop.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { codigoTiendaSugerido, CODIGO_TIENDA, limpiarCodigoTienda } from 'src/app/utils/codigo-tienda';
import Swal from 'sweetalert2';

/**
 * EL ALTA DE UNA TIENDA — pantalla propia, en `console/merchants/:code/tiendas/nueva`.
 *
 * 🔴 Era un modal dentro de la ficha del comercio. Regla de la casa: cada alta
 * es su propio componente con ruta propia, a ancho completo. Y además del
 * gusto hay un motivo: un modal no tiene dirección —no se comparte, no vuelve
 * con el botón de atrás y recargar la página pierde los cinco campos ya
 * escritos—.
 *
 * 🔴 Una tienda NO es un detalle del comercio: es el punto donde se origina una
 * venta, y de la tienda cuelga de qué comercio es esa venta. Por eso al crearla
 * desde aquí se le pone el comercio SIEMPRE, en la misma llamada — una tienda
 * sin comercio queda del lado de MundoTotal por la regla del proyecto, y eso,
 * hecho sin querer desde la ficha de EPA, sería un error muy difícil de
 * encontrar.
 *
 * ⚠️ Cero endpoints nuevos: el comercio sale de `GET /api/merchant/:code` (que
 * es de donde sale su nombre para la miga de pan) y el alta es
 * `POST /api/shop`, el mismo de siempre.
 */
@Component({
  selector: 'app-comercios-tienda-nueva',
  templateUrl: './comercios-tienda-nueva.component.html',
  styleUrls: ['./comercios-tienda-nueva.component.css']
})
export class ComerciosTiendaNuevaComponent implements OnInit {

  codigo = '';
  comercio: any = null;

  cargando = true;
  noEncontrado = false;
  guardando = false;

  form: any = { code: '', name: '', short_name: '', address: '', city: '' };

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _merchant: MerchantService,
    private _shop: ShopService,
    public _auth: AuthService
  ) { }

  /** El alta la puede hacer quien puede crear tiendas. La ruta sólo mira el
   *  perfil, así que el permiso fino se comprueba aquí: sin él se muestra por
   *  qué no se puede, en vez de un formulario que el servidor va a rechazar. */
  puedeCrear = (): boolean => this._auth.hasPermission('config.tiendas.create');

  ngOnInit(): void {
    this.ruta.paramMap.subscribe(p => {
      this.codigo = p.get('code') || '';
      this.cargar();
    });
  }

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.noEncontrado = false;
    try {
      const rep = await this._merchant.getMerchant(this.codigo);
      this.comercio = rep?.merchant || null;
      this.noEncontrado = !this.comercio;
      // El siguiente código libre sale de las tiendas que ya trae `GET /api/merchant/:code` (`shops[].code`).
      if (this.comercio && !this.form.code) {
        const codigos = (rep?.shops || rep?.merchant?.shops || []).map((t: any) => t?.code);
        this.form.code = codigoTiendaSugerido(this.codigo, codigos);
      }
    } catch {
      this.comercio = null;
      this.noEncontrado = true;
    } finally {
      this.cargando = false;
    }
  };

  /** El código cumple la regla (Juan, 2026-10-14). Vacío no cuenta como mal escrito: lo frena `valido`. */
  get codigoMal(): boolean {
    const c = limpiarCodigoTienda(this.form.code);
    return !!c && !CODIGO_TIENDA.test(c);
  }

  get valido(): boolean {
    return !!(this.form.code?.trim() && this.form.name?.trim()) && !this.codigoMal;
  }

  volverATiendas = (): void => {
    this.router.navigate(['/console/merchants', this.codigo, 'tiendas']);
  };

  crear = async (): Promise<void> => {
    if (!this.valido || this.guardando) return;
    this.guardando = true;
    try {
      await this._shop.createShop({
        code: limpiarCodigoTienda(this.form.code),
        name: this.form.name.trim(),
        short_name: (this.form.short_name || '').trim(),
        address: (this.form.address || '').trim(),
        city: (this.form.city || '').trim(),
        // 🔴 El comercio va en la misma llamada. Crearla y asignarla después son
        // dos pasos, y si el segundo falla queda una tienda huérfana vendiendo
        // a nombre de MundoTotal.
        merchant: this.codigo
      });
      await Swal.fire({ icon: 'success', title: 'Tienda creada',
                        text: `Ya está asignada a ${this.comercio?.name || this.codigo}.`,
                        timer: 2000, showConfirmButton: false });
      this.volverATiendas();
    } catch (e: any) {
      // La pantalla no se cierra: lo que se escribió se queda.
      Swal.fire({ icon: 'error', title: 'No se pudo crear la tienda',
                  text: e?.error?.msg || e?.message || 'Revisa el código: no puede repetirse.' });
    } finally {
      this.guardando = false;
    }
  };
}
